// PRE RMA CSB — Packs d'abonnement : tarifs et droits d'accès par module.
// Fonctions PURES, partagées par l'API et l'application : l'écran calcule les droits
// hors connexion, avec exactement la même règle que le serveur (voir offline/db.ts,
// store « abonnement »).
//
// Règles :
//  - mois gratuit : accès à TOUS les modules jusqu'à `gratuitJusquAu` (inclus) ;
//  - ensuite : chaque pack payé débloque ses modules jusqu'à sa `dateFin` (incluse) ;
//  - « Tout activer » (TOUT) débloque tout ;
//  - état inconnu (appareil qui n'a jamais pu joindre le serveur) : accès complet — on ne
//    bloque jamais un chef CSB en brousse faute d'avoir pu vérifier ;
//  - hors connexion avec un état expiré : tolérance de JOURS_TOLERANCE_HORS_LIGNE jours,
//    le temps de recevoir la confirmation d'un paiement déjà envoyé par MVola.
// Dates : chaînes « AAAA-MM-JJ » (comparables telles quelles).

export const PACKS = ['SANTE_BASE', 'GESTION_STOCK', 'ADMINISTRATION', 'TOUT'] as const;
export type Pack = (typeof PACKS)[number];

export const MODULES_ABONNEMENT = [
  'CONSULTATION', 'CPN', 'PEV', 'INTRANTS', 'INVENTAIRE', 'PREINVENTAIRE', 'FINANCE', 'COMMUNICATION', 'DECES',
] as const;
export type ModuleAbonnement = (typeof MODULES_ABONNEMENT)[number];

export const MODULES_PAR_PACK: Record<Exclude<Pack, 'TOUT'>, ModuleAbonnement[]> = {
  SANTE_BASE: ['CONSULTATION', 'CPN', 'PEV'],
  GESTION_STOCK: ['INTRANTS', 'INVENTAIRE', 'PREINVENTAIRE'],
  ADMINISTRATION: ['FINANCE', 'COMMUNICATION', 'DECES'],
};

export const PACK_LIBELLES: Record<Pack, string> = {
  SANTE_BASE: 'Pack Santé de Base',
  GESTION_STOCK: 'Pack Gestion de Stock',
  ADMINISTRATION: 'Pack Administration',
  TOUT: 'Option Tout Activer',
};

export const MODULE_LIBELLES: Record<ModuleAbonnement, string> = {
  CONSULTATION: 'Consultation', CPN: 'CPN', PEV: 'PEV', INTRANTS: 'Intrants', INVENTAIRE: 'Inventaire',
  PREINVENTAIRE: 'Pré-inventaire', FINANCE: 'Finance', COMMUNICATION: 'Communication', DECES: 'Décès',
};

/** Prix mensuel en Ariary. */
export const PRIX_MENSUEL_PACK: Record<Pack, number> = {
  SANTE_BASE: 3000, GESTION_STOCK: 2000, ADMINISTRATION: 1000, TOUT: 5000,
};

export const DUREES_MOIS = [1, 3] as const;
export type DureeMois = (typeof DUREES_MOIS)[number];

export const JOURS_TOLERANCE_HORS_LIGNE = 7;

export function estPack(v: unknown): v is Pack {
  return typeof v === 'string' && (PACKS as readonly string[]).includes(v);
}
export function estDuree(v: unknown): v is DureeMois {
  return typeof v === 'number' && (DUREES_MOIS as readonly number[]).includes(v);
}

/** Montant à payer : le trimestre = 3 × le prix mensuel (ex. Santé 9 000 Ar, Tout 15 000 Ar). */
export function prixPack(pack: Pack, dureeMois: DureeMois): number {
  return PRIX_MENSUEL_PACK[pack] * dureeMois;
}

/** Pack minimal à proposer pour débloquer un module. */
export function packPourModule(module: ModuleAbonnement): Exclude<Pack, 'TOUT'> {
  for (const p of ['SANTE_BASE', 'GESTION_STOCK', 'ADMINISTRATION'] as const) {
    if (MODULES_PAR_PACK[p].includes(module)) return p;
  }
  throw new Error(`Module inconnu : ${module}`);
}

export interface EtatAbonnement {
  gratuitJusquAu: string | null;
  packs: Array<{ pack: Pack; dateFin: string }>;
}

export interface AccesModules {
  /** true si le module est utilisable. */
  modules: Record<ModuleAbonnement, boolean>;
  /** Mois gratuit en cours (accès complet). */
  gratuit: boolean;
  /** Jours restants du mois gratuit (0 = dernier jour), null si terminé ou inconnu. */
  joursGratuitsRestants: number | null;
  /** Accès maintenu uniquement par la tolérance hors ligne. */
  enTolerance: boolean;
  /** État d'abonnement inconnu : accès complet par défaut. */
  inconnu: boolean;
}

function tousLesModules(valeur: boolean): Record<ModuleAbonnement, boolean> {
  const r = {} as Record<ModuleAbonnement, boolean>;
  for (const m of MODULES_ABONNEMENT) r[m] = valeur;
  return r;
}

/** Ajoute n jours à une date « AAAA-MM-JJ » (calcul UTC, sans décalage de fuseau). */
export function ajouterJours(date: string, n: number): string {
  const [a, m, j] = date.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j + n));
  return d.toISOString().slice(0, 10);
}

export function joursEntre(debut: string, fin: string): number {
  const [a1, m1, j1] = debut.split('-').map(Number);
  const [a2, m2, j2] = fin.split('-').map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, j2) - Date.UTC(a1, m1 - 1, j1)) / 86_400_000);
}

export function accesModules(etat: EtatAbonnement | null, aujourdhui: string, options: { horsLigne?: boolean } = {}): AccesModules {
  if (!etat) {
    return { modules: tousLesModules(true), gratuit: false, joursGratuitsRestants: null, enTolerance: false, inconnu: true };
  }
  const tolerance = options.horsLigne ? JOURS_TOLERANCE_HORS_LIGNE : 0;
  const jusqua = (fin: string | null, marge: number) => !!fin && aujourdhui <= ajouterJours(fin, marge);

  const gratuit = jusqua(etat.gratuitJusquAu, 0);
  const gratuitTolere = jusqua(etat.gratuitJusquAu, tolerance);
  const modules = tousLesModules(gratuitTolere);
  let enTolerance = gratuitTolere && !gratuit;

  if (!gratuit) {
    for (const { pack, dateFin } of etat.packs) {
      if (!jusqua(dateFin, tolerance)) continue;
      const strict = jusqua(dateFin, 0);
      const couverts = pack === 'TOUT' ? MODULES_ABONNEMENT : MODULES_PAR_PACK[pack];
      for (const m of couverts) {
        if (!modules[m]) { modules[m] = true; if (!strict) enTolerance = true; }
      }
    }
  }

  const joursGratuitsRestants = gratuit && etat.gratuitJusquAu ? joursEntre(aujourdhui, etat.gratuitJusquAu) : null;
  return { modules, gratuit, joursGratuitsRestants, enTolerance, inconnu: false };
}
